import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { HelpContent } from '@/hooks/useHelpContent';

interface HelpDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: HelpContent | null;
}

/**
 * Filters the markdown body so only paragraphs/headings matching the query
 * remain visible. Empty query → return full body.
 */
function filterByQuery(body: string, query: string): string {
  const q = query.trim().toLowerCase();
  if (!q) return body;
  const blocks = body.split(/\n\s*\n/);
  const matched = blocks.filter((b) => b.toLowerCase().includes(q));
  return matched.length > 0 ? matched.join('\n\n') : '_Sin resultados para tu búsqueda._';
}

export function HelpDrawer({ open, onOpenChange, content }: HelpDrawerProps) {
  const [query, setQuery] = useState('');

  // Reset query whenever the drawer opens or content changes
  useEffect(() => {
    if (open) setQuery('');
  }, [open, content?.route]);

  const filtered = useMemo(
    () => (content ? filterByQuery(content.body, query) : ''),
    [content, query],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'w-full sm:max-w-[480px] flex flex-col p-0 gap-0',
        )}
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b space-y-2">
          <div className="flex items-start justify-between gap-3">
            <SheetTitle className="text-base font-semibold leading-tight">
              {content?.title ?? 'Ayuda'}
            </SheetTitle>
          </div>
          <SheetDescription className="sr-only">
            Ayuda contextual de la pantalla actual.
          </SheetDescription>
          <div className="flex flex-wrap gap-1.5">
            {content?.module && (
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                {content.module}
              </Badge>
            )}
            {content?.tags?.slice(0, 4).map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px]">
                {tag}
              </Badge>
            ))}
          </div>
          <div className="relative pt-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en esta ayuda..."
              className="h-8 pl-8 text-sm"
              aria-label="Buscar en el contenido de ayuda"
            />
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-5 py-4 prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm">
            {content ? (
              <ReactMarkdown>{filtered}</ReactMarkdown>
            ) : (
              <p className="text-sm text-muted-foreground">No hay ayuda disponible.</p>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
